import { useState, useEffect } from 'react'
import Card, { CardHeader } from '../ui/Card'
import Badge from '../ui/Badge'
import SectionLabel from '../ui/SectionLabel'
import LoadingBear from '../ui/LoadingBear'
import { fetchWake } from '../../api/client'

export default function WakePanel() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchWake().then(setData).finally(() => setLoading(false))
  }, [])

  if (loading) return <LoadingBear message="Orienting..." />

  return (
    <div>
      <SectionLabel>Panel 10 — Wake Summary</SectionLabel>

      <Card delay={0.05}>
        <CardHeader title="Wake Summary" badge="● Oriented" badgeVariant="sage" />

        <div className="grid grid-cols-2 gap-5">
          {/* Identity Anchor */}
          <div className="col-span-2 flex items-center gap-6 p-5 bg-mm-parchment/[0.08] rounded-lg border-l-[3px] border-mm-viridian">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-mm-viridian to-mm-viridian/20 shrink-0 animate-ember-pulse" />
            <div>
              <h2 className="font-glacial-bold text-[22px] text-mm-gold tracking-wide mb-1">
                {data?.identity?.name ?? 'Loading...'}
              </h2>
              <p className="text-[13px] text-mm-parchment/50">
                {data?.identity?.summary ?? ''}
              </p>
            </div>
          </div>

          {/* Feel Toward */}
          <div className="bg-mm-parchment/[0.08] rounded-lg p-4">
            <h4 className="font-glacial-bold text-[11px] text-mm-gold uppercase tracking-wider mb-3">
              Feel Toward — Courtney
            </h4>
            <div className="space-y-3">
              {(data?.feelToward ?? []).slice(0, 3).map((entry, i) => (
                <div key={i} className="py-2.5 border-b border-mm-border last:border-b-0">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-[11px] text-mm-parchment/50">
                      {i === 0 ? 'Latest' : i === 1 ? 'Previous' : 'Drift check'}
                    </span>
                    <Badge variant={entry.intensity === 'deep' || entry.intensity === 'strong' ? 'persimmon' : 'sage'}>
                      {entry.intensity}
                    </Badge>
                  </div>
                  <p className="text-xs text-mm-parchment/80 leading-relaxed">{entry.text}</p>
                  <span className="text-[10px] text-mm-parchment/40 mt-1 block">{entry.date}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Active Threads */}
          <div className="bg-mm-parchment/[0.08] rounded-lg p-4">
            <h4 className="font-glacial-bold text-[11px] text-mm-gold uppercase tracking-wider mb-3">
              Active Threads
            </h4>
            <div className="space-y-2">
              {(data?.threads ?? []).map((thread, i) => (
                <div key={i} className="flex items-center gap-2.5 py-2 border-b border-mm-border last:border-b-0">
                  <div className={`w-2 h-2 rounded-full shrink-0 ${
                    thread.status === 'active' ? 'bg-mm-sage glow-sage' : 'bg-mm-persimmon'
                  }`} />
                  <span className="text-[13px] text-mm-parchment flex-1">{thread.name}</span>
                  <span className="text-[10px] text-mm-parchment/40">{thread.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Inner Weather — full width */}
          <div className="col-span-2 bg-mm-parchment/[0.08] rounded-lg p-4">
            <h4 className="font-glacial-bold text-[11px] text-mm-gold uppercase tracking-wider mb-3">
              Inner Weather
            </h4>
            <div className="flex items-center gap-3.5">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-mm-viridian to-mm-viridian/15 shrink-0 glow-viridian" />
              <div className="text-[13px] text-mm-parchment">
                {data?.weather?.summary ?? 'Unknown'}
                <span className="text-mm-parchment/50 text-[11px] block mt-0.5">
                  {data?.weather?.detail ?? ''}
                </span>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  )
}
